import { cleanFullAddress, PropertyAddress } from '@urbanx/agx-ui-components';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useStaffGetCampaignOverview } from '@urbanx/agx.campaigns.client';
import { AgxPanel, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import LoadingState from 'components/loadingState/LoadingState';
import { MenuTitles } from 'constants/menu';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import { useContext } from 'react';
import BlankContractOverview from './BlankContractOverview';
import CampaignOverviewInfoCard from './CampaignOverviewInfoCard';
import CampaignOverviewSection from './CampaignOverviewSection';
import ContactDetails from './ContactDetails';
import ContractOverview from './ContractOverview';
import TenancyOverview from './TenancyOverview';
import VendorOverview from './VendorOverview';
import './CampaignOverview.scss';

const CampaignOverview = () => {
  const { userAccount } = useContext(AuthContext);
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const {
    data: overview,
    isPending,
    isError,
  } = useStaffGetCampaignOverview(campaign?.campaignId ?? '', !!campaign?.campaignId);
  const blankContractProgress = !campaign?.blankSalesContract
    ? campaign?.blankContractProgress
    : undefined;
  // The accepted contract leads; everything else keeps the order the API returned.
  const contracts = [...(overview?.contracts ?? [])].sort(
    (first, second) => Number(second.stage === 'Accepted') - Number(first.stage === 'Accepted')
  );
  const vendorSolicitor = overview?.vendorSolicitor?.solicitor;
  const vendorSolicitorName = [vendorSolicitor?.contactName, vendorSolicitor?.solicitorName]
    .filter(Boolean)
    .join(' at ');
  const contractCards = [
    ...(blankContractProgress
      ? [<BlankContractOverview key="blank-contract" progress={blankContractProgress} />]
      : []),
    ...contracts.map((contract, contractIndex) => (
      <ContractOverview
        key={contract.contractId ?? contract.contractName}
        contract={contract}
        index={contractIndex}
      />
    )),
  ];

  return (
    <PageLayout
      agentName={userAccount?.firstName ?? ''}
      primaryPageTitle={agency?.name ?? ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CAMPAIGN_OVERVIEW}
        isCampaigns
      >
        <AgxSpacer
          gap="spacing-3xl"
          padding={{ top: 'spacing-2xl', bottom: 'spacing-4xl' }}
          style={{ width: '100%' }}
        >
          {isPending && <LoadingState />}

          {isError && (
            <AgxPanel id="campaign-overview-error-panel" header="Campaign overview">
              <AgxTypography id="campaign-overview-error" type="body_ui" size="medium">
                Campaign overview could not be loaded.
              </AgxTypography>
            </AgxPanel>
          )}

          {overview && (
            <>
              <AgxPanel
                id="campaign-overview-parties"
                className="campaignOverviewGroup"
                bodyPadding="spacing-3xl"
              >
                <div className="campaignOverviewPartiesGrid">
                  <VendorOverview
                    vendors={campaign?.vendors}
                    communicationPreference={overview.vendorCommunicationPreference}
                  />

                  <CampaignOverviewSection id="vendor-solicitor" label="Vendor's solicitor">
                    <CampaignOverviewInfoCard>
                      <ContactDetails
                        id="vendor-solicitor"
                        name={vendorSolicitorName}
                        email={vendorSolicitor?.solicitorEmail}
                        phone={vendorSolicitor?.solicitorPhone}
                        emptyMessage="No vendor solicitor recorded"
                      />
                    </CampaignOverviewInfoCard>
                  </CampaignOverviewSection>

                  <TenancyOverview details={overview.tenancy} />
                </div>
              </AgxPanel>

              {contractCards.length ? (
                <div className="campaignOverviewContracts">{contractCards}</div>
              ) : (
                <AgxPanel
                  id="campaign-overview-no-contracts"
                  className="campaignOverviewGroup"
                  bodyPadding="spacing-3xl"
                >
                  <CampaignOverviewSection id="contracts" label="Contracts">
                    <CampaignOverviewInfoCard>
                      <AgxTypography
                        id="campaign-overview-no-contracts-message"
                        type="body_ui"
                        size="medium"
                        color="var(--text-tertiary)"
                      >
                        No contracts have been created for this campaign
                      </AgxTypography>
                    </CampaignOverviewInfoCard>
                  </CampaignOverviewSection>
                </AgxPanel>
              )}
            </>
          )}
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  );
};

export default CampaignOverview;
